/**
 * SortManager.ts
 *
 * Todo 리스트 정렬 관련 기능을 관리하는 모듈
 *
 * 주요 기능:
 * - 다양한 정렬 옵션 제공 (Today, Due Date, Priority, Created)
 * - 정렬 로직 캡슐화
 *
 * sort options
 *  1. Due date - 마감일 순 정렬 (마감일 없는 항목은 뒤로)
 *  2. Reminder date - 리마인더 날짜 순 정렬 (알람이 없는 항목은 뒤로)
 *  3. A-Z (Title) - 제목 알파벳 순 정렬 (Default)
 *  4. Created - 생성일 순 정렬 (오래된 순)
 */

import i18n from 'i18n'
import moment from 'moment'
import { FilterType, SortType } from './TodoFilter'
import { TodoItem } from './TodoItem'

/**
 * 주어진 날짜가 오늘인지 확인하는 함수
 */
export const isToday = (dateStr: string | Date | null): boolean => {
  if (!dateStr) return false

  const date = new Date(dateStr.toString())
  const today = new Date()

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

/**
 * 날짜가 유효한지 확인하는 함수
 */
const isValidDate = (date: Date): boolean => {
  return !isNaN(date.getTime())
}

/**
 * Due Date 정렬 함수
 * - dueDate가 있는 TodoItem들을 날짜순으로 정렬 (오름차순)
 * - dueDate가 없는 TodoItem들은 정렬된 항목들 뒤쪽으로 배치
 */
const sortByDueDate = (a: TodoItem, b: TodoItem, ascending: boolean = true): number => {
  // dueDate가 없는 경우를 먼저 처리
  // dueDate가 없는 항목은 항상 마지막에 위치 (ascending/descending 관계없이)
  if (!a.dueDate && !b.dueDate) return 0 // 둘 다 dueDate가 없으면 순서 유지
  if (!a.dueDate) return 1 // a만 dueDate가 없으면 뒤로
  if (!b.dueDate) return -1 // b만 dueDate가 없으면 뒤로

  // dueDate가 있는 경우 날짜 비교
  const dateA = new Date(a.dueDate.toString())
  const dateB = new Date(b.dueDate.toString())

  // 유효하지 않은 날짜 처리 (항상 마지막에 위치)
  if (!isValidDate(dateA) && !isValidDate(dateB)) return 0
  if (!isValidDate(dateA)) return 1
  if (!isValidDate(dateB)) return -1

  // 유효한 날짜는 ascending 파라미터에 따라 정렬
  const diff = dateA.getTime() - dateB.getTime()
  return ascending ? diff : -diff
}

/**
 * Created 정렬 함수
 *  - 오래된 날짜가 먼저 오도록
 */
const sortByCreated = (a: TodoItem, b: TodoItem, ascending: boolean = true): number => {
  const dateA = new Date(a.dtstamp || new Date(0))
  const dateB = new Date(b.dtstamp || new Date(0))

  if (!isValidDate(dateA) && !isValidDate(dateB)) return 0
  if (!isValidDate(dateA)) return 1
  if (!isValidDate(dateB)) return -1

  const diff = dateA.getTime() - dateB.getTime()
  return ascending ? diff : -diff
}

const sortByCompleted = (a: TodoItem, b: TodoItem, ascending: boolean = true): number => {
  const dateA = new Date(a.completedDate || new Date(0))
  const dateB = new Date(b.completedDate || new Date(0))

  if (!isValidDate(dateA) && !isValidDate(dateB)) return 0
  if (!isValidDate(dateA)) return 1
  if (!isValidDate(dateB)) return -1

  const diff = dateA.getTime() - dateB.getTime()
  return ascending ? diff : -diff
}

/**
 * Reminder 정렬 함수
 * - reminder 설정된 항목이 먼저 오고, 날짜 순으로 정렬
 */
const sortByReminder = (a: TodoItem, b: TodoItem, ascending: boolean = true): number => {
  // reminder가 없는 경우를 먼저 처리
  // reminder가 없는 항목은 항상 마지막에 위치 (ascending/descending 관계없이)
  const aHasReminder = a.alarms && a.alarms.length > 0
  const bHasReminder = b.alarms && b.alarms.length > 0

  if (!aHasReminder && !bHasReminder) return 0 // 둘 다 reminder가 없으면 순서 유지
  if (!aHasReminder) return 1 // a만 reminder가 없으면 뒤로
  if (!bHasReminder) return -1 // b만 reminder가 없으면 뒤로

  // reminder가 있는 경우 시간 비교
  const aReminderTime = getEarliestReminderTime(a)
  const bReminderTime = getEarliestReminderTime(b)

  // 유효하지 않은 reminder 시간 처리 (항상 마지막에 위치)
  if (!aReminderTime && !bReminderTime) return 0
  if (!aReminderTime) return 1
  if (!bReminderTime) return -1

  // 유효한 reminder 시간은 ascending 파라미터에 따라 정렬
  const diff = aReminderTime.getTime() - bReminderTime.getTime()
  return ascending ? diff : -diff
}

/**
 * TodoItem 에서 earliest reminder time 을 가져온다.
 */
const getEarliestReminderTime = (todo: TodoItem): Date | null => {
  if (!todo.alarms || todo.alarms.length === 0) return null

  const reminderTimes: Date[] = []

  for (const alarm of todo.alarms) {
    try {
      if (typeof alarm.trigger === 'string' && alarm.trigger.match(/^[+-]?PT/)) {
        // 상대 시간 - Due date 또는 Start date 기준
        const baseDate = todo.dueDate || todo.startDate
        if (baseDate) {
          const baseDateObj = new Date(baseDate)
          const durationMs = parseDurationToMs(alarm.trigger)
          const reminderTime = new Date(baseDateObj.getTime() + durationMs)
          if (isValidDate(reminderTime)) {
            reminderTimes.push(reminderTime)
          }
        }
      } else if (typeof alarm.trigger === 'object' && alarm.trigger.val) {
        // 절대 시간 - iCalendar 형식을 JavaScript Date로 안전하게 변환
        const reminderTime = parseICalendarDateForReminder(alarm.trigger.val)
        if (reminderTime && isValidDate(reminderTime)) {
          reminderTimes.push(reminderTime)
        }
      }
    } catch {
      // 파싱 에러 무시
      continue
    }
  }

  if (reminderTimes.length === 0) return null

  // 가장 이른 시간 반환
  return new Date(Math.min(...reminderTimes.map((d) => d.getTime())))
}

/**
 * Duration 문자열을 밀리초로 변환 (예: "-PT15M" -> -900000)
 */
const parseDurationToMs = (duration: string): number => {
  // 간단한 Duration 파싱 (PT15M, PT1H 등)
  const match = duration.match(/^-?PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/)
  if (!match) return 0

  const hours = parseInt(match[1] || '0', 10)
  const minutes = parseInt(match[2] || '0', 10)
  const seconds = parseInt(match[3] || '0', 10)

  const totalMs = (hours * 3600 + minutes * 60 + seconds) * 1000
  return duration.startsWith('-') ? -totalMs : totalMs
}
/**
 * iCalendar 날짜 형식을 JavaScript Date로 변환 (알람용)
 * @param dateValue iCalendar 날짜 값 (예: '20250818T160000Z')
 * @returns Date 객체 또는 null
 */
const parseICalendarDateForReminder = (dateValue: string): Date | null => {
  if (!dateValue) return null

  try {
    // UTC 형식 (Z로 끝나는 경우): '20250818T160000Z'
    if (dateValue.endsWith('Z')) {
      const dateStr = dateValue.slice(0, -1) // Z 제거
      if (dateStr.includes('T')) {
        // moment를 사용하여 UTC로 파싱
        const parsed = moment.utc(dateStr, 'YYYYMMDDTHHmmss')
        return parsed.isValid() ? parsed.toDate() : null
      }
    }

    // 타임존 없는 날짜시간: '20250818T160000'
    else if (dateValue.includes('T')) {
      const parsed = moment(dateValue, 'YYYYMMDDTHHmmss')
      return parsed.isValid() ? parsed.toDate() : null
    }

    // 날짜만: '20250818'
    else if (dateValue.length === 8) {
      const parsed = moment(dateValue, 'YYYYMMDD')
      return parsed.isValid() ? parsed.toDate() : null
    }

    // 기타 형식 시도
    const parsed = moment(dateValue)
    return parsed.isValid() ? parsed.toDate() : null
  } catch (error) {
    console.warn('Failed to parse reminder date:', dateValue, error)
    return null
  }
}

/**
 * Title 정렬 함수 (A-Z 알파벳 순)
 * - 대소문자 구분 없이 알파벳 순으로 정렬
 */
const sortByTitle =
  (locale: string, ascending: boolean = true) =>
  (a: TodoItem, b: TodoItem): number => {
    const titleA = (a.summary || '').toLowerCase()
    const titleB = (b.summary || '').toLowerCase()

    const diff = titleA.localeCompare(titleB, locale)
    return ascending ? diff : -diff
  }

/**
 * Planned 정렬 함수 - Due date 최신순, 같으면 Reminder 최신순, 같으면 Alphabet ASC
 */
const sortByPlanned =
  (locale: string, ascending: boolean = true) =>
  (a: TodoItem, b: TodoItem): number => {
    // 1. Due date 비교
    const dueDateCompare = sortByDueDate(a, b, ascending)
    if (dueDateCompare !== 0) return dueDateCompare

    // 2. Due date가 같으면 Reminder 비교
    const reminderCompare = sortByReminder(a, b, ascending)
    if (reminderCompare !== 0) return reminderCompare

    // 3. Reminder도 같으면 제목 알파벳 순
    return sortByTitle(locale, ascending)(a, b)
  }

/**
 * Overdue 정렬 함수 - Due date 최신순, 같으면 Reminder 최신순, 같으면 Alphabet ASC
 */
const sortByOverdue =
  (locale: string, ascending: boolean = true) =>
  (a: TodoItem, b: TodoItem): number => {
    // 1. Due date 비교
    const dueDateCompare = sortByDueDate(a, b, ascending)
    if (dueDateCompare !== 0) return dueDateCompare

    // 2. Due date가 같으면 Reminder 비교
    const reminderCompare = sortByReminder(a, b, ascending)
    if (reminderCompare !== 0) return reminderCompare

    // 3. Reminder도 같으면 제목 알파벳 순
    return sortByTitle(locale, ascending)(a, b)
  }

/**
 * 메인 정렬 함수
 * @param todoList - 정렬할 todo 항목들
 * @param sortType - 정렬 타입 (SortType enum)
 * @param ascending - 오름차순 여부 (기본값: true)
 * @returns 정렬된 todo 항목들
 */
export const sortTodos = (
  todoList: TodoItem[],
  sortType: SortType,
  ascending: boolean = true,
  locale: string = 'en',
): TodoItem[] => {
  let sortedTodos = [...todoList]

  switch (sortType) {
    case SortType.DueDate:
      sortedTodos = sortedTodos.sort((a, b) => sortByDueDate(a, b, ascending))
      break

    case SortType.Reminder:
      sortedTodos = sortedTodos.sort((a, b) => sortByReminder(a, b, ascending))
      break

    case SortType.Title:
      sortedTodos = sortedTodos.sort((a, b) => sortByTitle(locale, ascending)(a, b))
      break

    case SortType.Created:
      sortedTodos = sortedTodos.sort((a, b) => sortByCreated(a, b, ascending))
      break

    case SortType.Planned:
      sortedTodos = sortedTodos.sort((a, b) => sortByPlanned(locale, ascending)(a, b))
      break

    case SortType.Overdue:
      sortedTodos = sortedTodos.sort((a, b) => sortByOverdue(locale, ascending)(a, b))
      break

    case SortType.Completed:
      sortedTodos = sortedTodos.sort((a, b) => sortByCompleted(a, b, ascending)) // 완료된 항목은 완료일 순
      break

    default:
      return sortedTodos
  }

  return sortedTodos
}

/**
 * 각 필터의 기본 정렬 옵션 반환 (Redux settings와 동일)
 */
export const getDefaultSortOption = (currentFilter: FilterType): SortType => {
  switch (currentFilter) {
    case FilterType.All:
      return SortType.Title
    case FilterType.Today:
      return SortType.Created
    case FilterType.FocusDate:
      return SortType.Created // Today filter와 동일한 정렬 옵션 사용
    case FilterType.Important:
      return SortType.DueDate
    case FilterType.Planned:
      return SortType.Planned
    case FilterType.Overdue:
      return SortType.Overdue
    case FilterType.Completed:
      return SortType.Completed
    default:
      return SortType.Created
  }
}

/**
 * FilterType 에 따른 SortType list menu 를 반환.
 */
export const getSortOptions = (currentFilter: FilterType): { key: SortType; label: string }[] => {
  switch (currentFilter) {
    case FilterType.All:
      return [
        { key: SortType.DueDate, label: i18n.t('todo.todolist.dueDate') },
        { key: SortType.Reminder, label: i18n.t('todo.todolist.reminderDate') },
        { key: SortType.Title, label: i18n.t('todo.todolist.titleSort') },
        { key: SortType.Created, label: i18n.t('todo.todolist.created') },
      ]
    case FilterType.Today:
    case FilterType.FocusDate:
      return [
        { key: SortType.Reminder, label: i18n.t('todo.todolist.reminderDate') },
        { key: SortType.Title, label: i18n.t('todo.todolist.titleSort') },
        { key: SortType.Created, label: i18n.t('todo.todolist.created') },
      ]
    case FilterType.Important:
      return [
        { key: SortType.DueDate, label: i18n.t('todo.todolist.dueDate') },
        { key: SortType.Reminder, label: i18n.t('todo.todolist.reminderDate') },
        { key: SortType.Title, label: i18n.t('todo.todolist.titleSort') },
        { key: SortType.Created, label: i18n.t('todo.todolist.created') },
      ]
    case FilterType.Planned:
      return [{ key: SortType.Planned, label: '' }]
    case FilterType.Overdue:
      return [{ key: SortType.Overdue, label: '' }]
    case FilterType.Completed:
      return [{ key: SortType.Completed, label: i18n.t('todo.todolist.completedDate') }]
    default:
      return []
  }
}
